import styles from './TiktokInfluencerPostPaymentRequestModal.module.css';
import { PostPaymentRequest, TiktokInfluencerPost, TiktokInfluencerUser, postPostPaymentRequest } from '@round/api';
import { Button, Modal, ModalTitle } from '@round/ui-kit';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import { asMoney, showNotification } from 'helpers';
import useNonNullContext from 'Hooks/useNonNullContext';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import PostPaymentRequestForm, { PaymentRequestFormValues } from '../components/PostPaymentRequestForm';
import { baseInitialValues, getPaymentMethodAPIValues, validationSchema } from '../helpers';

type Props = {
    influencerPost: TiktokInfluencerPost | null;
    influencer: TiktokInfluencerUser | null;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (paymentRequest: PostPaymentRequest) => void;
};

const hasNumericalMismatch = (a: number | undefined | null, b: number | undefined | null) => {
    if (typeof a !== 'number' || typeof b !== 'number') {
        return false;
    }

    return a !== b;
};

const TiktokInfluencerPostPaymentRequestModal = ({ isOpen, onClose, onSuccess, influencerPost, influencer }: Props) => {
    const { currencies } = useNonNullContext(OptionsContext);
    const currencyOptions = currencies.map((c) => ({
        label: c.name,
        value: c.id,
    }));

    const handleSubmit = async (values: PaymentRequestFormValues, helpers: FormikHelpers<PaymentRequestFormValues>) => {
        if (!influencerPost?.id || !values.currency?.value) {
            return;
        }

        helpers.setSubmitting(true);
        try {
            const response = await postPostPaymentRequest({
                amount: values.amount,
                currency: values.currency.value,
                tiktok_influencer_post: influencerPost.id,
                ...getPaymentMethodAPIValues(values),
            });

            if (response.status === 201) {
                showNotification('Payment request created', 'info');
                onSuccess(response.data);
                onClose();
                return;
            }

            if (response.status === 400) {
                if (Array.isArray(response.data)) {
                    showNotification(response.data.join('. '), 'error');
                    return;
                }

                helpers.setErrors(mapApiErrorsToFormikErrors<PaymentRequestFormValues>(response.data));
                return;
            }

            showNotification(response.data.detail, 'error');
        } catch (error) {
            showNotification('Could not request payment', 'error');
        } finally {
            helpers.setSubmitting(false);
        }
    };

    const initialValues: PaymentRequestFormValues = {
        ...baseInitialValues,
        paypal_email_address: influencer?.paypal_email || '',
        amount: influencerPost?.cost || '',
        currency: currencyOptions.find((opt) => opt.value === influencerPost?.currency_id),
    };

    const brandPromoCurrency = currencies.find((curr) => curr.id === influencer?.brand_promotion_currency);
    const brandPromoRateDisplayValue =
        influencer?.brand_promotion_cost && brandPromoCurrency
            ? asMoney(parseFloat(influencer.brand_promotion_cost), brandPromoCurrency)
            : '-';

    const soundPromoCurrency = currencies.find((curr) => curr.id === influencer?.currency);
    const soundPromoRateDisplayValue =
        influencer?.cost && soundPromoCurrency ? asMoney(parseFloat(influencer.cost), soundPromoCurrency) : '-';

    const postCurrency = currencies.find((curr) => curr.id === influencerPost?.currency_id);
    const postCostDisplayValue =
        influencerPost?.cost && postCurrency ? asMoney(parseFloat(influencerPost.cost), postCurrency) : '-';

    const formikProps = useFormik<PaymentRequestFormValues>({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    });

    const currencyFieldValue = formikProps.values.currency?.value;

    const hasCurrencyMismatch =
        hasNumericalMismatch(currencyFieldValue, influencer?.brand_promotion_currency) ||
        hasNumericalMismatch(currencyFieldValue, influencer?.currency);

    const handleClose = () => {
        formikProps.resetForm();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick className={styles.container}>
            <ModalTitle>Request Payment</ModalTitle>
            <dl className={styles.detailsContainer}>
                <div>
                    <dt>Sound promo rate</dt>
                    <dd>{soundPromoRateDisplayValue}</dd>
                </div>

                <div>
                    <dt>Brand promo rate</dt>
                    <dd>{brandPromoRateDisplayValue}</dd>
                </div>

                <div>
                    <dt>Post cost</dt>
                    <dd>{postCostDisplayValue}</dd>
                </div>
            </dl>

            <FormikProvider value={formikProps}>
                <>
                    <PostPaymentRequestForm />

                    <Button
                        type="filled"
                        color="black"
                        loading={formikProps.isSubmitting}
                        disabled={!formikProps.isValid}
                        onClick={formikProps.submitForm}
                        className={styles.submitButton}
                    >
                        Submit
                    </Button>

                    {hasCurrencyMismatch && (
                        <p className={styles.warning}>
                            The selected currency does not match the influencer's requested currency.
                        </p>
                    )}
                </>
            </FormikProvider>
        </Modal>
    );
};

export default TiktokInfluencerPostPaymentRequestModal;
